'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MolarityCalculator() {
  const t = useTranslations("tools.molarityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    mass: '',
    massUnit: 'g',
    molecularWeight: '',
    mwUnit: 'gm',
    volume: '',
    volumeUnit: 'L',
    concentration: '',
    concentrationUnit: 'M'
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const massConversions: { [key: string]: number } = {
    't': 1000000, 'kg': 1000, 'g': 1, 'mg': 0.001, 'ug': 0.000001,
    'Da': 1.66053906660e-24, 'lb': 453.59237, 'oz': 28.349523125
  };

  const volumeConversions: { [key: string]: number } = {
    'm': 1000, 'cm': 0.001, 'mm': 0.000001, 'L': 1, 'mL': 0.001,
    'gal': 3.785411784, 'qt': 0.946352946, 'pt': 0.473176473,
    'c': 0.2365882365, 'fl oz': 0.0295735295625, 'yd': 764.554857984,
    'ft': 28.316846592, 'in': 0.016387064
  };

  const mwConversions: { [key: string]: number } = {
    'gm': 1, 'kgm': 1000
  };

  const concentrationConversions: { [key: string]: number | null } = {
    'M': 1, 'mM': 0.001, 'uM': 0.000001, 'nM': 0.000000001, 'molm': 0.001,
    'kgl': null, 'gl': null, 'mgl': null, 'ppm': null, 'ppb': null,
    'kgm': null, 'gm': null
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const mass = parseFloat(inputs.mass);
    const mw = parseFloat(inputs.molecularWeight);
    const volume = parseFloat(inputs.volume);
    const concentration = parseFloat(inputs.concentration);

    const filledFields = [!isNaN(mass), !isNaN(mw), !isNaN(volume), !isNaN(concentration)].filter(Boolean).length;

    if (filledFields !== 3) {
      setError(t('errorThreeValues'));
      return;
    }

    const massInGrams = isNaN(mass) ? null : mass * massConversions[inputs.massUnit];
    const mwInGMol = isNaN(mw) ? null : mw * mwConversions[inputs.mwUnit];
    const volumeInLiters = isNaN(volume) ? null : volume * volumeConversions[inputs.volumeUnit];
    const concentrationInM = isNaN(concentration) ? null : concentration * (concentrationConversions[inputs.concentrationUnit] || 1);

    let resultMass: number, resultMW: number, resultVolume: number, resultConcentration: number, resultMoles: number;

    if (massInGrams === null) {
      resultConcentration = concentrationInM!;
      resultMW = mwInGMol!;
      resultVolume = volumeInLiters!;
      resultMass = resultConcentration * resultMW * resultVolume;
      resultMoles = resultConcentration * resultVolume;
    } else if (mwInGMol === null) {
      resultMass = massInGrams;
      resultConcentration = concentrationInM!;
      resultVolume = volumeInLiters!;
      resultMW = resultMass / (resultConcentration * resultVolume);
      resultMoles = resultConcentration * resultVolume;
    } else if (volumeInLiters === null) {
      resultMass = massInGrams;
      resultMW = mwInGMol;
      resultConcentration = concentrationInM!;
      resultVolume = resultMass / (resultConcentration * resultMW);
      resultMoles = resultConcentration * resultVolume;
    } else {
      resultMass = massInGrams;
      resultMW = mwInGMol;
      resultVolume = volumeInLiters;
      resultMoles = resultMass / resultMW;
      resultConcentration = resultMoles / resultVolume;
    }

    if (resultConcentration <= 0 || resultMass <= 0 || resultMW <= 0 || resultVolume <= 0) {
      setError(t('errorInvalidCalculation'));
      return;
    }

    setResults({
      molarity: resultConcentration,
      mass: resultMass,
      moles: resultMoles
    });
  };

  const handleClear = () => {
    setInputs({
      mass: '',
      massUnit: 'g',
      molecularWeight: '',
      mwUnit: 'gm',
      volume: '',
      volumeUnit: 'L',
      concentration: '',
      concentrationUnit: 'M'
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formulaDisplayStyle = {
    background: '#f8fafc',
    borderLeft: '4px solid #2563eb',
    padding: '1.5rem',
    marginBottom: '2rem',
    borderRadius: '0.5rem'
  };

  const formulaTextStyle = getResponsiveStyle(
    { margin: 0, textAlign: 'center' as const, color: '#334155' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const formGridStyle = {
    display: 'grid',
    gap: '1.5rem'
  };

  const formGroupStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', alignItems: 'center' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '150px 1fr auto' },
    { gridTemplateColumns: '150px 1fr auto' }
  );

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155' },
    { textAlign: 'left' as const },
    { textAlign: 'right' as const },
    { textAlign: 'right' as const }
  );

  const inputStyle = {
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    width: '100%'
  };

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%'
    },
    {},
    { minWidth: '200px' },
    { minWidth: '200px' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2.5rem',
      fontSize: '1.1rem',
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2.5rem',
      fontSize: '1.1rem',
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac'
    },
    { margin: '1.5rem', padding: '1.5rem' },
    { margin: '2rem', padding: '2rem' },
    { margin: '2rem', padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#166534', textAlign: 'center' as const },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '1rem'
  };

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      border: '2px solid #fca5a5',
      color: '#991b1b',
      borderRadius: '0.5rem',
      textAlign: 'center' as const
    },
    { margin: '1rem 1.5rem', padding: '1rem' },
    { margin: '1rem 2rem', padding: '1rem' },
    { margin: '1rem 2rem', padding: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    paddingLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🧪 {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formulaDisplayStyle}>
              <p style={formulaTextStyle}>
                {t('formulaText')}
              </p>
            </div>

            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="mass">{t('massLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="mass"
                  step="any"
                  placeholder={t('massPlaceholder')}
                  value={inputs.mass}
                  onChange={(e) => setInputs({...inputs, mass: e.target.value})}
                />
                <select
                  style={selectStyle}
                  id="mass-unit"
                  value={inputs.massUnit}
                  onChange={(e) => setInputs({...inputs, massUnit: e.target.value})}
                >
                  <option value="t">{t('massUnitTon')}</option>
                  <option value="kg">{t('massUnitKg')}</option>
                  <option value="g">{t('massUnitG')}</option>
                  <option value="mg">{t('massUnitMg')}</option>
                  <option value="ug">{t('massUnitUg')}</option>
                  <option value="Da">{t('massUnitDa')}</option>
                  <option value="lb">{t('massUnitLb')}</option>
                  <option value="oz">{t('massUnitOz')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="mw">{t('mwLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="mw"
                  step="any"
                  placeholder={t('mwPlaceholder')}
                  value={inputs.molecularWeight}
                  onChange={(e) => setInputs({...inputs, molecularWeight: e.target.value})}
                />
                <select
                  style={selectStyle}
                  id="mw-unit"
                  value={inputs.mwUnit}
                  onChange={(e) => setInputs({...inputs, mwUnit: e.target.value})}
                >
                  <option value="gm">{t('mwUnitGm')}</option>
                  <option value="kgm">{t('mwUnitKgm')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="volume">{t('volumeLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="volume"
                  step="any"
                  placeholder={t('volumePlaceholder')}
                  value={inputs.volume}
                  onChange={(e) => setInputs({...inputs, volume: e.target.value})}
                />
                <select
                  style={selectStyle}
                  id="volume-unit"
                  value={inputs.volumeUnit}
                  onChange={(e) => setInputs({...inputs, volumeUnit: e.target.value})}
                >
                  <option value="m">{t('volumeUnitM3')}</option>
                  <option value="cm">{t('volumeUnitCm3')}</option>
                  <option value="mm">{t('volumeUnitMm3')}</option>
                  <option value="L">{t('volumeUnitL')}</option>
                  <option value="mL">{t('volumeUnitMl')}</option>
                  <option value="gal">{t('volumeUnitGal')}</option>
                  <option value="qt">{t('volumeUnitQt')}</option>
                  <option value="pt">{t('volumeUnitPt')}</option>
                  <option value="c">{t('volumeUnitC')}</option>
                  <option value="fl oz">{t('volumeUnitFlOz')}</option>
                  <option value="yd">{t('volumeUnitYd3')}</option>
                  <option value="ft">{t('volumeUnitFt3')}</option>
                  <option value="in">{t('volumeUnitIn3')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="concentration">{t('concentrationLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="concentration"
                  step="any"
                  placeholder={t('concentrationPlaceholder')}
                  value={inputs.concentration}
                  onChange={(e) => setInputs({...inputs, concentration: e.target.value})}
                />
                <select
                  style={selectStyle}
                  id="concentration-unit"
                  value={inputs.concentrationUnit}
                  onChange={(e) => setInputs({...inputs, concentrationUnit: e.target.value})}
                >
                  <optgroup label={t('concentrationGroupMolarity')}>
                    <option value="M">{t('concentrationUnitM')}</option>
                    <option value="mM">{t('concentrationUnitMm')}</option>
                    <option value="uM">{t('concentrationUnitUm')}</option>
                    <option value="nM">{t('concentrationUnitNm')}</option>
                    <option value="molm">{t('concentrationUnitMolm')}</option>
                  </optgroup>
                  <optgroup label={t('concentrationGroupMass')}>
                    <option value="kgl">{t('concentrationUnitKgl')}</option>
                    <option value="gl">{t('concentrationUnitGl')}</option>
                    <option value="mgl">{t('concentrationUnitMgl')}</option>
                    <option value="ppm">{t('concentrationUnitPpm')}</option>
                    <option value="ppb">{t('concentrationUnitPpb')}</option>
                    <option value="kgm">{t('concentrationUnitKgm')}</option>
                    <option value="gm">{t('concentrationUnitGm')}</option>
                  </optgroup>
                </select>
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearButtonStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultMolarity')}</span>
                <span style={resultValueStyle}>{results.molarity.toExponential(4)} M</span>
              </div>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultMass')}</span>
                <span style={resultValueStyle}>{results.mass.toFixed(4)} g</span>
              </div>
              <div style={{...resultItemStyle, marginBottom: 0}}>
                <span style={resultLabelStyle}>{t('resultMoles')}</span>
                <span style={resultValueStyle}>{results.moles.toExponential(4)} mol</span>
              </div>
            </div>
          )}

          {error && (
            <div style={errorMessageStyle}>{error}</div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
          <li style={listItemStyle}>{t('insight6')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsPara1')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsPara2')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsPara3')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsPara4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.omnicalculator.com/chemistry/molarity" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/molarity-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.sigmaaldrich.com/US/en/support/calculators-and-apps/mass-molarity-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://chem.libretexts.org/Courses/Oregon_Institute_of_Technology/OIT:_CHE_201_-_General_Chemistry_I_(Anthony_and_Clark)/Unit_6:_Common_Chemical_Reactions/6.1:_Calculating_Molarity" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.khanacademy.org/science/chemistry/states-of-matter-and-intermolecular-forces/mixtures-and-solutions/a/molarity" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}